/*
 * PDP-Liefertimeline: Bestellen → Versand → Lieferung, mit Countdown bis zum Bestellschluss.
 *
 * Markup: snippets/altruan-liefer-timeline.liquid. Die Shipping-App (POST /public-shipping-date)
 * liefert das fertige label ("Lieferung Do, 08.10. – Fr, 09.10. · nach 84028") und den
 * Bestellschluss order_cutoff_at. Lieferort (Land, PLZ, Ort) und Endpoint kommen aus
 * window.AltruanLieferort (snippets/altruan-lieferort-head.liquid); ein neuer Lieferort lädt neu.
 */
(() => {
  "use strict";

  if (customElements.get("altruan-liefer-timeline")) return;

  const URGENT_MS = 60 * 60 * 1000;
  const REFETCH_GUARD_MS = 10 * 1000;
  const BAR_REFERENCE_MS = 24 * 60 * 60 * 1000;

  class AltruanLieferTimeline extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector("script[data-liefer-data]");
      if (!dataEl) return;

      try {
        this.config = JSON.parse(dataEl.textContent);
      } catch (_) {
        return;
      }

      this.dateEls = this.querySelectorAll("[data-liefer-date]");
      this.countdownEl = this.querySelector("[data-liefer-countdown]");
      this.barEl = this.querySelector("[data-liefer-bar]");

      this.deadline = null;
      this.lastFetch = 0;

      this.load();
      this.timer = setInterval(() => this.tick(), 1000);
      this.watchVariant();

      // Neuer Lieferort: sofort neu laden (bewusste Aktion, daher ohne Sperrzeit)
      this.onLieferort = () => {
        this.lastFetch = 0;
        this.load();
      };
      document.addEventListener("altruan-lieferort:change", this.onLieferort);
    }

    disconnectedCallback() {
      if (this.timer) clearInterval(this.timer);
      if (this.unsubscribeVariant) this.unsubscribeVariant();
      document.removeEventListener("altruan-lieferort:change", this.onLieferort);
    }

    // B2B-Mail-Link: SKU im Betreff/Text bei Variantenwechsel aktualisieren.
    // Das Snippet wird vom Theme nicht neu gerendert, daher per variant-change-Event.
    watchVariant() {
      this.b2bLink = this.querySelector("[data-liefer-b2b-link]");
      if (!this.b2bLink || typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") return;

      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event && event.data && event.data.variant;
        if (!variant) return;
        if (this.config.productId && variant.product_id && String(variant.product_id) !== String(this.config.productId))
          return;
        this.updateB2bLink(variant.sku);
      });
    }

    updateB2bLink(sku) {
      const { b2bMail, b2bSubjectTpl, b2bBodyTpl } = this.config;
      if (!b2bMail || !b2bSubjectTpl) return;
      const safeSku = (sku || "").trim() || "–";
      const subject = b2bSubjectTpl.replace(/__SKU__/g, safeSku);
      const body = (b2bBodyTpl || "").replace(/__SKU__/g, safeSku).replace(/\r?\n/g, "\r\n");
      this.b2bLink.href =
        `mailto:${b2bMail}?subject=${encodeURIComponent(subject)}` + (body ? `&body=${encodeURIComponent(body)}` : "");
    }

    async load() {
      const now = Date.now();
      if (now - this.lastFetch < REFETCH_GUARD_MS) return;
      this.lastFetch = now;

      try {
        const shopify = window.Shopify || {};
        const lieferort = window.AltruanLieferort || null;
        const place = lieferort ? lieferort.get() : null;
        const body = {
          // Land aus dem Auswahlfenster, sonst der Shop-Markt (assets/altruan-lieferdatum.js genauso)
          country: (place && place.country) || shopify.country || this.config.country,
          locale: shopify.locale || this.config.locale,
          variant_id: Number(this.config.defaultVariantId),
          total_inventory: this.config.totalInventory,
          variant_always_available: this.config.alwaysAvailable,
        };
        if (place && place.postal_code) body.postal_code = place.postal_code;
        if (place && place.postal_code && place.city) body.city = place.city;

        const response = await fetch((lieferort && lieferort.endpoint) || this.config.endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) return this.fallback();

        // Antwort der App: fertiges label + Bestellschluss als ISO-Zeitstempel, kein Text zum Parsen.
        const data = await response.json();
        const deadline = Date.parse(data.order_cutoff_at);
        if (typeof data.label !== "string" || !data.label || Number.isNaN(deadline)) return this.fallback();

        this.applyLabel(data.label);
        this.applyDeadline(deadline);
        this.classList.remove("is-loading", "is-fallback");
      } catch (_) {
        this.fallback();
      }
    }

    // Das label enthält schon "Lieferung", daher den Schritt-Titel ("Lieferung zwischen") ausblenden
    applyLabel(label) {
      this.dateEls.forEach((el) => {
        el.textContent = label;
        const title = el.closest(".altruan-liefer__step-text")?.querySelector(".altruan-liefer__step-title");
        if (title) title.hidden = true;
      });
    }

    // Bestellschluss als absoluter Zeitpunkt (ms seit Epoch). Liegt er laut Geräteuhr schon zurück
    // (Uhr geht vor), Countdown ausblenden statt immer wieder neu zu laden.
    applyDeadline(deadline) {
      const running = deadline > Date.now();
      this.deadline = running ? deadline : null;
      if (this.countdownEl) this.countdownEl.closest("p").hidden = !running;
      if (this.barEl) this.barEl.hidden = !running;
      if (running) this.tick();
    }

    tick() {
      if (!this.deadline) return;
      const rest = this.deadline - Date.now();

      // Bestellschluss erreicht: neu laden (nächster Liefertag). Die Sperrzeit in load() bleibt aktiv.
      if (rest <= 0) {
        this.deadline = null;
        this.load();
        return;
      }

      const pad = (n) => String(n).padStart(2, "0");
      const h = Math.floor(rest / 3600000);
      const m = Math.floor((rest % 3600000) / 60000);
      const s = Math.floor((rest % 60000) / 1000);
      if (this.countdownEl) this.countdownEl.textContent = `${pad(h)}:${pad(m)}:${pad(s)}`;

      if (this.barEl) {
        const pct = Math.max(1, Math.min(100, (rest / BAR_REFERENCE_MS) * 100));
        this.barEl.style.setProperty("--liefer-remaining", `${pct}%`);
      }

      this.classList.toggle("is-urgent", rest < URGENT_MS);
    }

    fallback() {
      this.classList.remove("is-loading");
      this.classList.add("is-fallback");
      this.deadline = null;
    }
  }

  customElements.define("altruan-liefer-timeline", AltruanLieferTimeline);
})();
